import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ui/confirm-dialog";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import type { AuthMemberModel } from "@/types/authType";
import { authAPI } from "@/services/authAPI";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member?: AuthMemberModel | null;
  onUpdated?: () => void; // called after successful update
};

export default function SetNewPasswordModal({ open, onOpenChange, member, onUpdated }: Props) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const newPasswordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => newPasswordRef.current?.focus(), 150);
    } else {
      setNewPassword("");
      setConfirmPassword("");
      setPending(false);
    }
  }, [open]);

  const validatePassword = (p: string) => {
    if (!p || p.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(p)) return "Password must include at least one uppercase letter";
    if (!/[0-9]/.test(p)) return "Password must include at least one number";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(p))
      return "Password must include at least one special character";
    return null;
  };

  const handleUpdatePassword = async () => {
    const err = validatePassword(newPassword);
    if (err) {
      toast.error(err);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!member) {
      toast.error("Missing member context");
      return;
    }
    // perform the API call and manage pending state; this function will be
    // called when the user confirms in the ConfirmDialog.
    setPending(true);
    try {
      const resp = await authAPI.updatePassword({
        memberno: member.memberno,
        userpass: newPassword,
        updatedby: member.memberno,
      });
      if (!resp || !resp.isSuccess) {
        toast.error(resp?.errorMessages || "Failed to update password");
        return;
      }
      toast.success("Password updated. Please sign in.");
      onOpenChange(false);
      onUpdated?.();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Failed to update password");
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <div className="p-4">
          <h3 className="text-lg font-semibold">Set your new password</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Your account requires a password to be set before you can access the system.
          </p>
          <div className="mt-4">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">New password</span>
              <div className="relative">
                <Input
                  ref={newPasswordRef}
                  type={showNewPassword ? "text" : "password"}
                  name="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                  onClick={() => setShowNewPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? "Hide" : "Show"}
                </button>
              </div>
            </label>
            <label className="block text-sm mt-3">
              <span className="mb-1 block font-medium">Confirm password</span>
              <div className="relative">
                <Input
                  name="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  aria-label={
                    showConfirmPassword ? "Hide confirm password" : "Show confirm password"
                  }
                  onClick={() => setShowConfirmPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
            </label>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={() => setOpenConfirm(true)} disabled={pending}>
              {pending ? "Saving..." : "Update password"}
            </Button>
          </div>

          <ConfirmDialog
            open={openConfirm}
            onOpenChange={setOpenConfirm}
            ContentIcon={AlertTriangle}
            contentIconBgClass="cdms-primary-soft"
            contentIconColorClass="text-primary"
            title="Confirm password change"
            description="Are you sure you want to set this new password? This will update the account password immediately."
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            confirmVariant="success"
            onConfirm={() => {
              // ConfirmDialog will close itself before calling this; start update
              void handleUpdatePassword();
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
